import { Box, Grid, TextField, Theme, Typography } from '@material-ui/core';
import { Label, Menu as MenuIcon } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { useEffect, useState } from 'react';
import { ConsoleActionButton } from '../../common/ConsoleActionButton';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useOrgInfo } from '../../../../../global/auth/use-org-info/use-org-info';

type LabelField = { key: string; label: string; placeholder: string };

const WORD_FIELDS: LabelField[] = [
  { key: 'wordActivity', label: 'Activity', placeholder: 'Activity' },
  { key: 'wordRoster', label: 'Roster', placeholder: 'Roster' },
  { key: 'wordProgram', label: 'Program', placeholder: 'Program' },
];

const MENU_SECTIONS: { title: string; fields: LabelField[] }[] = [
  {
    title: 'Volunteer dashboard',
    fields: [
      { key: 'dashboardHome', label: 'Home', placeholder: 'My Elements' },
      { key: 'dashboardResources', label: 'Resources', placeholder: 'Resources' },
      { key: 'dashboardImpact', label: 'Impact', placeholder: 'Impact Dashboard' },
    ],
  },
  {
    title: 'Volunteering',
    fields: [
      { key: 'volunteeringActivities', label: 'Explore', placeholder: 'Explore' },
      { key: 'volunteeringActivityReport', label: 'Activity report', placeholder: 'Activity Report' },
      { key: 'volunteeringIncidentReport', label: 'Incident report', placeholder: 'Incident Report' },
    ],
  },
  {
    title: 'Management',
    fields: [
      { key: 'managementDashboard', label: 'Dashboard', placeholder: 'Dashboard' },
      { key: 'managementActivityManagement', label: 'Activity management', placeholder: 'Activity Management' },
      { key: 'managementRosters', label: 'Rosters', placeholder: 'Rosters' },
      { key: 'managementVolunteers', label: 'Volunteers', placeholder: 'Volunteers' },
      { key: 'managementBulletins', label: 'Bulletins', placeholder: 'Bulletins' },
      { key: 'managementReportManagement', label: 'Report management', placeholder: 'Report Management' },
      { key: 'managementIncidentManagement', label: 'Incident management', placeholder: 'Incident Management' },
    ],
  },
  {
    title: 'Admin',
    fields: [
      { key: 'adminTaskManagement', label: 'Task management', placeholder: 'Task Management' },
      { key: 'adminFormsSurveys', label: 'Forms & surveys', placeholder: 'Forms & Surveys' },
      { key: 'adminProgramManagement', label: 'Program management', placeholder: 'Program Management' },
      { key: 'adminConsole', label: 'Admin console', placeholder: 'Admin Console' },
    ],
  },
];

const ALL_KEYS = [...WORD_FIELDS, ...MENU_SECTIONS.flatMap((section) => section.fields)].map((field) => field.key);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    contentContainer: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    heading: {
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    sectionTitle: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    sectionDescription: {
      fontSize: '14px',
      color: '#808080',
      marginTop: '0.25rem',
      marginBottom: '1.5rem',
    },
    groupTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: theme.color.grey.neutral400,
      marginBottom: 12,
    },
    groupBlock: {
      marginBottom: '1.75rem',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    field: {
      '&& .MuiOutlinedInput-root': {
        borderRadius: 6,
        backgroundColor: '#FFFFFF',
        fontSize: '0.8rem',
      },
      '&& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#e0e0e0',
      },
      '&& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.color.grey.neutral200,
      },
      '&& .MuiInputLabel-root': {
        fontSize: '0.8rem',
      },
    },
    saveButton: {
      backgroundColor: theme.color.secondary.main900,
      color: '#ffffff',
      padding: '7px 12px',
      minWidth: 40,
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: '1.5rem',
    },
  })
);

const AdminConsoleLabelsPanel = () => {
  const classes = useStyles();
  const apiUrl = useApiUrl();
  const orgInfo = useOrgInfo();
  const { setSnackbar } = useSnackbar();
  const saveLabels = useCampfireFetch({ defer: true });

  const [values, setValues] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<Record<string, string>>({});

  useEffect(() => {
    const menuLabels = orgInfo?.menuLabels;
    const next: Record<string, string> = {};

    ALL_KEYS.forEach((key) => {
      next[key] = (menuLabels as Record<string, string | null | undefined> | undefined)?.[key] || '';
    });

    setValues(next);
    setSaved(next);
  }, [orgInfo?.menuLabels]);

  const isDirty = ALL_KEYS.some((key) => (values[key] || '') !== (saved[key] || ''));

  const handleSave = async () => {
    const labels: Record<string, string | null> = {};
    ALL_KEYS.forEach((key) => {
      labels[key] = values[key]?.trim() ? values[key] : null;
    });

    try {
      const response = await saveLabels.run({
        url: `${apiUrl}/tm/config/menu-labels`,
        method: 'post',
        data: { labels },
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      setSaved(values);
      setSnackbar({ variant: 'success', open: true, message: 'Settings updated' });
    } catch (error) {
      setSnackbar({ variant: 'error', open: true, message: 'Could not save labels' });
    }
  };

  const renderField = (field: LabelField) => (
    <Grid item xs={12} sm={6} md={4} key={field.key}>
      <TextField
        fullWidth
        size='small'
        variant='outlined'
        className={classes.field}
        label={field.label}
        placeholder={field.placeholder}
        value={values[field.key] || ''}
        onChange={(e) => {
          const { value } = e.target;
          setValues((prev) => ({ ...prev, [field.key]: value }));
        }}
        inputProps={{ maxLength: 255, style: { fontSize: '0.8rem' } }}
        InputLabelProps={{ shrink: true }}
      />
    </Grid>
  );

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Typography variant='h2' className={classes.headingTitle}>
          Custom Labels
        </Typography>
        <Typography variant={'body2'}>{'Manage the wording your organisation uses across the product'}</Typography>
      </Box>

      <Box className={classes.contentContainer}>
        <Typography className={classes.sectionTitle}>
          <Label style={{ fontSize: '14px', paddingRight: '5px' }} />
          Word Replacements
        </Typography>
        <Typography variant={'body2'} className={classes.sectionDescription}>
          Replace common words wherever they appear. Leave a field blank to keep the Volaby default shown in grey
        </Typography>
        <Grid container spacing={2}>
          {WORD_FIELDS.map(renderField)}
        </Grid>
      </Box>

      <Box className={classes.contentContainer}>
        <Typography className={classes.sectionTitle}>
          <MenuIcon style={{ fontSize: '14px', paddingRight: '5px' }} />
          Navigation Labels
        </Typography>
        <Typography variant={'body2'} className={classes.sectionDescription}>
          Rename the items in each navigation menu
        </Typography>

        {MENU_SECTIONS.map((section) => (
          <Box key={section.title} className={classes.groupBlock}>
            <Typography className={classes.groupTitle}>{section.title}</Typography>
            <Grid container spacing={2}>
              {section.fields.map(renderField)}
            </Grid>
          </Box>
        ))}

        <Box className={classes.actions}>
          <ConsoleActionButton
            variant='contained'
            color='primary'
            className={classes.saveButton}
            disabled={!isDirty || saveLabels.isLoading}
            onClick={handleSave}
          >
            Save Labels
          </ConsoleActionButton>
        </Box>
      </Box>
    </Box>
  );
};

export { AdminConsoleLabelsPanel };
